var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { createAutoGroup, setState } from '../helpers';
import { themeFromProps } from '../themes';
import { autoCenteredText, fitFontSizeToWidth, fluidFont, resolveBounds, textYForBaseline, } from '../layout';
export function panelTheme(props) {
    return themeFromProps(props);
}
export function panelBounds(props, dw, dh) {
    if (dw === void 0) { dw = 220; }
    if (dh === void 0) { dh = 130; }
    return resolveBounds(props, dw, dh);
}
export function panelGroup(app, type, props, bounds) {
    return createAutoGroup(app, type, __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), type);
}
export function addPanelFrame(group, app, bounds, theme, compact) {
    if (compact === void 0) { compact = false; }
    var pad = bounds.pad, w = bounds.innerWidth, h = bounds.innerHeight;
    group.add(app.roundedRect({
        x: pad,
        y: pad,
        width: w,
        height: h,
        cornerRadius: Math.min(10, h * 0.1),
        fill: '#111827',
        stroke: theme.dialStroke,
        strokeWidth: compact ? 1 : 1.5,
        listening: false,
    }));
}
/** Absolute Y for text anchor (middle baseline). */
export function textAt(anchorY, fontSize) {
    return textYForBaseline(anchorY, fontSize);
}
export function addPanelTitle(group, app, bounds, theme, title) {
    var pad = bounds.pad;
    var size = fluidFont(8, bounds, 6, 9);
    var ty = pad + (bounds.innerHeight < 72 ? 8 : 10);
    group.add(app.text({
        text: title.toUpperCase(),
        x: pad + 2,
        y: textAt(ty, size),
        fontSize: size,
        fontWeight: 'bold',
        fill: theme.textMuted,
        textAlign: 'left',
        listening: false,
    }));
    return ty + size + 6;
}
export function addProgressBar(group, app, x, y, w, h, progress, theme) {
    var p = Math.max(0, Math.min(1, progress));
    group.add(app.roundedRect({
        x: x,
        y: y,
        width: w,
        height: h,
        cornerRadius: h / 2,
        fill: '#1f2937',
        listening: false,
    }), app.roundedRect({
        x: x,
        y: y,
        width: Math.max(h, w * p),
        height: h,
        cornerRadius: h / 2,
        fill: theme.accent,
        listening: false,
    }));
}
export function addIconTile(group, app, x, y, size, icon, active, theme, label) {
    group.add(app.roundedRect({
        x: x,
        y: y,
        width: size,
        height: size,
        cornerRadius: Math.min(8, size * 0.22),
        fill: active ? theme.accent : '#1f2937',
        stroke: active ? theme.accent : theme.dialStroke,
        strokeWidth: 1,
        listening: false,
    }));
    var iconSize = Math.max(7, Math.floor(size * (label ? 0.28 : 0.36)));
    var iconY = label ? size * 0.32 : size * 0.5;
    group.add(autoCenteredText(app, icon, size, iconY, {
        fontSize: iconSize,
        fontWeight: 'bold',
        fill: active ? '#fff' : theme.textMuted,
        insetX: x,
        insetY: y,
    }));
    if (label) {
        var labelSize = Math.max(5, Math.floor(size * 0.16));
        group.add(autoCenteredText(app, label, size, size * 0.76, {
            fontSize: labelSize,
            fill: active ? '#dbeafe' : theme.textMuted,
            insetX: x,
            insetY: y,
        }));
    }
}
function addSkipPrev(group, app, x, y, size, theme) {
    group.add(app.roundedRect({
        x: x,
        y: y,
        width: size,
        height: size,
        cornerRadius: Math.min(8, size * 0.22),
        fill: '#1f2937',
        stroke: theme.dialStroke,
        strokeWidth: 1,
        listening: false,
    }), app.path({
        d: "M ".concat(x + size * 0.28, " ").concat(y + size * 0.35, " L ").concat(x + size * 0.18, " ").concat(y + size * 0.5, " L ").concat(x + size * 0.28, " ").concat(y + size * 0.65),
        stroke: theme.text,
        strokeWidth: 1.5,
        lineCap: 'round',
        lineJoin: 'round',
        listening: false,
    }), app.path({
        d: "M ".concat(x + size * 0.42, " ").concat(y + size * 0.35, " L ").concat(x + size * 0.32, " ").concat(y + size * 0.5, " L ").concat(x + size * 0.42, " ").concat(y + size * 0.65),
        stroke: theme.text,
        strokeWidth: 1.5,
        lineCap: 'round',
        lineJoin: 'round',
        listening: false,
    }));
}
function addSkipNext(group, app, x, y, size, theme) {
    group.add(app.roundedRect({
        x: x,
        y: y,
        width: size,
        height: size,
        cornerRadius: Math.min(8, size * 0.22),
        fill: '#1f2937',
        stroke: theme.dialStroke,
        strokeWidth: 1,
        listening: false,
    }), app.path({
        d: "M ".concat(x + size * 0.58, " ").concat(y + size * 0.35, " L ").concat(x + size * 0.68, " ").concat(y + size * 0.5, " L ").concat(x + size * 0.58, " ").concat(y + size * 0.65),
        stroke: theme.text,
        strokeWidth: 1.5,
        lineCap: 'round',
        lineJoin: 'round',
        listening: false,
    }), app.path({
        d: "M ".concat(x + size * 0.72, " ").concat(y + size * 0.35, " L ").concat(x + size * 0.82, " ").concat(y + size * 0.5, " L ").concat(x + size * 0.72, " ").concat(y + size * 0.65),
        stroke: theme.text,
        strokeWidth: 1.5,
        lineCap: 'round',
        lineJoin: 'round',
        listening: false,
    }));
}
export function addTransportRow(group, app, cx, y, w, h, theme, playing) {
    if (playing === void 0) { playing = true; }
    var btn = Math.max(22, Math.min(h - 2, w * 0.14));
    var gap = Math.max(10, w * 0.06);
    var playSize = Math.min(Math.max(btn, 28), h, w * 0.18);
    var left = cx - playSize / 2 - gap - btn;
    var btnY = y + (h - btn) / 2;
    var playX = cx - playSize / 2;
    var playY = y + (h - playSize) / 2;
    addSkipPrev(group, app, left, btnY, btn, theme);
    group.add(app.circle({
        x: playX,
        y: playY,
        radius: playSize / 2,
        fill: theme.accent,
        listening: false,
    }));
    if (playing) {
        var barW = Math.max(2, playSize * 0.1);
        var barH = playSize * 0.28;
        var mid = playX + playSize / 2;
        var midY = playY + playSize / 2;
        group.add(app.roundedRect({
            x: mid - barW - 2,
            y: midY - barH / 2,
            width: barW,
            height: barH,
            cornerRadius: 1,
            fill: '#fff',
            listening: false,
        }), app.roundedRect({
            x: mid + 2,
            y: midY - barH / 2,
            width: barW,
            height: barH,
            cornerRadius: 1,
            fill: '#fff',
            listening: false,
        }));
    }
    else {
        group.add(app.path({
            d: "M ".concat(playX + playSize * 0.38, " ").concat(playY + playSize * 0.3, " L ").concat(playX + playSize * 0.38, " ").concat(playY + playSize * 0.7, " L ").concat(playX + playSize * 0.68, " ").concat(playY + playSize * 0.5, " Z"),
            fill: '#fff',
            listening: false,
        }));
    }
    addSkipNext(group, app, cx + playSize / 2 + gap, btnY, btn, theme);
}
export function addAlbumPlaceholder(group, app, x, y, size, accent) {
    if (accent === void 0) { accent = '#6366f1'; }
    group.add(app.roundedRect({
        x: x,
        y: y,
        width: size,
        height: size,
        cornerRadius: Math.min(10, size * 0.1),
        fill: accent,
        stroke: 'rgba(255,255,255,0.12)',
        strokeWidth: 1,
        listening: false,
    }));
    var note = Math.max(10, size * 0.28);
    group.add(autoCenteredText(app, '♪', size, size / 2, {
        fontSize: note,
        fontWeight: 'bold',
        fill: 'rgba(255,255,255,0.9)',
        insetX: x,
        insetY: y,
    }));
}
export function addMediaArtAndMeta(group, app, bounds, theme, top, title, artist, accent) {
    var pad = bounds.pad, w = bounds.innerWidth, h = bounds.innerHeight;
    var art = Math.max(40, Math.min(w * 0.32, h * 0.48, 72));
    addAlbumPlaceholder(group, app, pad, top, art, accent);
    var tx = pad + art + 10;
    var tw = w - art - 14;
    var titleSize = fluidFont(12, bounds, 9, 14);
    var titleFit = fitFontSizeToWidth(title, tw, titleSize, 8);
    group.add(app.text({
        text: title,
        x: tx,
        y: textAt(top + 4, titleFit.fontSize),
        fontSize: titleFit.fontSize,
        fontWeight: 'bold',
        fill: theme.text,
        textAlign: 'left',
        listening: false,
    }));
    var subSize = fluidFont(9, bounds, 7, 10);
    group.add(app.text({
        text: artist,
        x: tx,
        y: textAt(top + titleFit.fontSize + 8, subSize),
        fontSize: subSize,
        fill: theme.textMuted,
        textAlign: 'left',
        listening: false,
    }));
    return { artSize: art, bottom: top + art };
}
export function addListRow(group, app, x, y, w, h, icon, text, meta, theme, accent) {
    if (accent === void 0) { accent = false; }
    group.add(app.roundedRect({
        x: x,
        y: y,
        width: w,
        height: h,
        cornerRadius: 6,
        fill: '#1a2332',
        listening: false,
    }));
    var iconBox = Math.max(18, h - 8);
    addIconTile(group, app, x + 4, y + (h - iconBox) / 2, iconBox, icon, accent, theme);
    var textX = x + iconBox + 10;
    var textW = w - iconBox - (meta ? 30 : 14);
    var row = fitLabel(app, text, textX, y + h * 0.28, textW, 9, theme, accent);
    if (accent)
        row.fill = theme.warning;
    group.add(row);
    if (meta) {
        group.add(app.text({
            text: meta,
            x: x + w - 26,
            y: textAt(y + h * 0.62, 8),
            fontSize: 8,
            fill: theme.textMuted,
            textAlign: 'left',
            listening: false,
        }));
    }
}
export function lonLatToTile(lon, lat, zoom) {
    var n = Math.pow(2, zoom);
    var x = Math.floor(((lon + 180) / 360) * n);
    var latRad = (lat * Math.PI) / 180;
    var y = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n);
    return { x: x, y: y };
}
export function osmTileUrl(zoom, x, y) {
    return "https://tile.openstreetmap.org/".concat(zoom, "/").concat(x, "/").concat(y, ".png");
}
export function addOsmMapLayer(group, app, x, y, w, h, theme, options) {
    var e_1, _a;
    var _b, _c, _d;
    if (options === void 0) { options = {}; }
    var lat = (_b = options.lat) !== null && _b !== void 0 ? _b : 51.505;
    var lon = (_c = options.lon) !== null && _c !== void 0 ? _c : -0.09;
    var zoom = (_d = options.zoom) !== null && _d !== void 0 ? _d : 14;
    group.add(app.roundedRect({
        x: x,
        y: y,
        width: w,
        height: h,
        cornerRadius: Math.min(8, h * 0.12),
        fill: '#16231b',
        stroke: theme.dialStroke,
        strokeWidth: 1,
        listening: false,
    }));
    group.add(app.roundedRect({
        x: x + w * 0.06,
        y: y + h * 0.1,
        width: w * 0.24,
        height: h * 0.22,
        cornerRadius: 3,
        fill: '#1f3d2e',
        listening: false,
    }), app.roundedRect({
        x: x + w * 0.66,
        y: y + h * 0.55,
        width: w * 0.26,
        height: h * 0.2,
        cornerRadius: 3,
        fill: '#1a3350',
        listening: false,
    }));
    var roads = [
        { d: "M ".concat(x + 6, " ").concat(y + h * 0.52, " L ").concat(x + w - 6, " ").concat(y + h * 0.46), w: 3, c: '#5c6b7a' },
        { d: "M ".concat(x + w * 0.22, " ").concat(y + 6, " L ").concat(x + w * 0.3, " ").concat(y + h - 6), w: 2, c: '#4a5568' },
        { d: "M ".concat(x + w * 0.64, " ").concat(y + 6, " L ").concat(x + w * 0.56, " ").concat(y + h - 6), w: 2, c: '#4a5568' },
        { d: "M ".concat(x + 6, " ").concat(y + h * 0.26, " L ").concat(x + w - 6, " ").concat(y + h * 0.3), w: 1.5, c: '#3d4a57' },
    ];
    try {
        for (var roads_1 = __values(roads), roads_1_1 = roads_1.next(); !roads_1_1.done; roads_1_1 = roads_1.next()) {
            var r = roads_1_1.value;
            group.add(app.path({
                d: r.d,
                stroke: r.c,
                strokeWidth: r.w,
                lineCap: 'round',
                listening: false,
            }));
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (roads_1_1 && !roads_1_1.done && (_a = roads_1.return)) _a.call(roads_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    if (options.route !== false) {
        var routeD = "M ".concat(x + w * 0.14, " ").concat(y + h * 0.76, " Q ").concat(x + w * 0.44, " ").concat(y + h * 0.34, " ").concat(x + w * 0.84, " ").concat(y + h * 0.2);
        group.add(app.path({
            d: routeD,
            stroke: 'rgba(37,99,235,0.35)',
            strokeWidth: Math.max(5, w * 0.028),
            lineCap: 'round',
            listening: false,
        }), app.path({
            d: routeD,
            stroke: theme.accent,
            strokeWidth: Math.max(2.5, w * 0.014),
            lineCap: 'round',
            listening: false,
        }));
    }
    if (options.marker !== false) {
        var mx = x + w * 0.74;
        var my = y + h * 0.24;
        group.add(app.circle({
            x: mx - 5,
            y: my - 5,
            radius: 5,
            fill: theme.accent,
            stroke: '#fff',
            strokeWidth: 1.5,
            listening: false,
        }));
    }
    if (options.useTile !== false && w >= 48 && h >= 36) {
        var tile = lonLatToTile(lon, lat, zoom);
        var img = app.image({
            x: x,
            y: y,
            width: w,
            height: h,
            src: osmTileUrl(zoom, tile.x, tile.y),
            opacity: 0.5,
            listening: false,
        });
        group.add(img);
        img.load().then(function () { return app.requestRender(); }).catch(function () { return undefined; });
    }
    group.add(app.text({
        text: '© OSM',
        x: x + w - 28,
        y: y + h - 11,
        fontSize: 7,
        fill: 'rgba(255,255,255,0.5)',
        listening: false,
    }));
}
export function addCompassRose(group, app, cx, cy, radius, heading, theme) {
    var e_2, _a;
    group.add(app.circle({
        x: cx - radius,
        y: cy - radius,
        radius: radius,
        fill: '#0f172a',
        stroke: theme.dialStroke,
        strokeWidth: 1.5,
        listening: false,
    }));
    for (var deg = 0; deg < 360; deg += 30) {
        var rad_1 = ((deg - 90) * Math.PI) / 180;
        var inner = radius * (deg % 90 === 0 ? 0.78 : 0.86);
        var outer = radius * 0.94;
        var ix = Math.cos(rad_1) * inner;
        var iy = Math.sin(rad_1) * inner;
        var ox = Math.cos(rad_1) * outer;
        var oy = Math.sin(rad_1) * outer;
        group.add(app.line({
            x: cx + ix,
            y: cy + iy,
            x2: ox - ix,
            y2: oy - iy,
            stroke: '#334155',
            strokeWidth: deg % 90 === 0 ? 1.5 : 0.8,
            listening: false,
        }));
    }
    var labels = [
        { t: 'N', deg: 0, c: theme.warning },
        { t: 'E', deg: 90, c: theme.textMuted },
        { t: 'S', deg: 180, c: theme.textMuted },
        { t: 'W', deg: 270, c: theme.textMuted },
    ];
    var labelSize = Math.max(7, radius * 0.2);
    try {
        for (var labels_1 = __values(labels), labels_1_1 = labels_1.next(); !labels_1_1.done; labels_1_1 = labels_1.next()) {
            var _b = labels_1_1.value, t = _b.t, deg = _b.deg, c = _b.c;
            var rad_2 = ((deg - 90) * Math.PI) / 180;
            var lx = cx + Math.cos(rad_2) * (radius * 0.68);
            var ly = cy + Math.sin(rad_2) * (radius * 0.68);
            group.add(app.text({
                text: t,
                x: lx,
                y: textAt(ly, labelSize),
                fontSize: labelSize,
                fontWeight: t === 'N' ? 'bold' : 'normal',
                fill: c,
                textAlign: 'center',
                listening: false,
            }));
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (labels_1_1 && !labels_1_1.done && (_a = labels_1.return)) _a.call(labels_1);
        }
        finally { if (e_2) throw e_2.error; }
    }
    var needleLen = radius * 0.58;
    var rad = ((heading - 90) * Math.PI) / 180;
    group.add(app.line({
        x: cx,
        y: cy,
        x2: Math.cos(rad) * needleLen,
        y2: Math.sin(rad) * needleLen,
        stroke: theme.accent,
        strokeWidth: Math.max(2.5, radius * 0.09),
        lineCap: 'round',
        listening: false,
    }));
    group.add(app.circle({
        x: cx - 3,
        y: cy - 3,
        radius: 3,
        fill: theme.text,
        listening: false,
    }));
}
export function finishPanel(group, props, bounds, extra) {
    if (extra === void 0) { extra = {}; }
    setState(group, __assign(__assign(__assign({}, props), extra), { width: bounds.width, height: bounds.height }));
    return group;
}
export function fitLabel(app, text, x, y, maxW, maxSize, theme, bold) {
    if (bold === void 0) { bold = false; }
    var fit = fitFontSizeToWidth(text, maxW, maxSize, 7);
    return app.text({
        text: text,
        x: x + fit.x,
        y: textAt(y, fit.fontSize),
        fontSize: fit.fontSize,
        fontWeight: bold ? 'bold' : 'normal',
        fill: theme.text,
        textAlign: 'left',
        listening: false,
    });
}
export function addTurnArrow(group, app, x, y, size, theme) {
    group.add(app.roundedRect({
        x: x,
        y: y,
        width: size,
        height: size,
        cornerRadius: 8,
        fill: theme.accent,
        listening: false,
    }), app.path({
        d: "M ".concat(x + size * 0.28, " ").concat(y + size * 0.32, " L ").concat(x + size * 0.62, " ").concat(y + size * 0.5, " L ").concat(x + size * 0.28, " ").concat(y + size * 0.68, " M ").concat(x + size * 0.62, " ").concat(y + size * 0.5, " L ").concat(x + size * 0.62, " ").concat(y + size * 0.32, " L ").concat(x + size * 0.78, " ").concat(y + size * 0.5, " L ").concat(x + size * 0.62, " ").concat(y + size * 0.68),
        stroke: '#fff',
        strokeWidth: 2.2,
        lineCap: 'round',
        lineJoin: 'round',
        listening: false,
    }));
}
